<template>
  <div class="list-doe-container">
    <b-loading data-cy="LOADING" :isFullPage="false" :active="isLoading" />
    <div class="has-text-weight-semibold px-15 py-10 is-size-7 alert">
      <template v-if="finishedProcessAssembly">
        <span>{{ App.ProcessViewer.SNACK_FINISH }}</span>
        <b-button
          type="is-ghost"
          size="is-small"
          :label="General.CLICK_HERE"
          @click="downloadProcess"
        />
      </template>
      <template v-else>{{
        failedProcessAssembly
          ? App.ProcessViewer.DOWNLOAD_FAIL
          : App.ProcessViewer.SNACK_DESCRIPTION
      }}</template>
    </div>
    <ul>
      <li
        v-for="(step, index) in App.ProcessViewer.SNACK_STEP"
        :key="index"
        class="item"
      >
        <span class="pl-10">{{ step }}</span>
        <b-icon
          v-if="actualStep > getStepIndex(index)"
          :icon="failedProcessAssembly ? 'alert-circle' : 'check-circle'"
          :type="failedProcessAssembly ? 'is-danger' : 'is-green-lawn'"
        />
        <CircleLoading v-else endless />
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { App, General } from "@/utils/constants";
import CircleLoading from "@/components/CircleLoading";
import { NUP, applyMask } from "@/utils/protocols";
import {
  PROCESS_DOWNLOAD_STATUS_BACK as STATUS_BACK,
  PROCESS_DOWNLOAD_STATUS as STATUS
} from "@/utils/enumerate";
import { SnackQueue as Queue } from "@/plugins/SnackQueue";
import { downloadFile } from "@/utils/fileUtility";
import debounce from "lodash/debounce";

const maxNotificationWaitTime = 60e3;

export default {
  name: "ProcessDownloadStep",
  components: {
    CircleLoading
  },

  data() {
    return {
      App,
      General,
      STATUS,
      STATUS_BACK,
      isLoading: false
    };
  },

  computed: {
    ...mapGetters(["getProcessDownloadState"]),
    finishedProcessAssembly() {
      return this.processDownloadState === STATUS_BACK.FINISHED;
    },
    failedProcessAssembly() {
      return this.processDownloadState === STATUS_BACK.ERROR;
    },
    processId() {
      return this.$attrs.processId;
    },
    processDownloadState() {
      return this.getProcessDownloadState(this.processId)?.status;
    },
    actualStep() {
      return this.getStepIndex(this.processDownloadState);
    }
  },

  watch: {
    processDownloadState: {
      handler(newState) {
        const IS_ERROR = newState === STATUS_BACK.ERROR;
        const IS_FINISHED = newState === STATUS_BACK.FINISHED;

        if (IS_FINISHED || IS_ERROR) {
          Queue.open({
            id: `download_process:${this.processId}`,
            canClose: true
          });
          if (IS_FINISHED) {
            this.downloadProcess();
          } else {
            this.$emit("updateTitle", App.ProcessViewer.SNACK_TITLE_FAIL);
          }
        } else this.checkStep();
      },
      immediate: true
    }
  },

  beforeMount() {
    const title = App.ProcessViewer.SNACK_TITLE;
    const nupMask = applyMask(NUP, this.$attrs.nup);
    this.$emit("updateTitle", title.replace("(NUP)", nupMask));
  },

  async mounted() {
    const result = await this.downloadProcessFile(this.processId);
    if (result?.download_url) {
      this.updateProcessDownloadState({
        process_id: this.processId,
        status: STATUS_BACK.FINISHED,
        download_url: result.download_url
      });
    }
  },

  methods: {
    ...mapActions(["getProcessBuildState", "downloadProcessFile"]),
    ...mapMutations(["updateProcessDownloadState"]),

    getStepIndex(step) {
      return Object.keys(STATUS).indexOf(step);
    },

    downloadProcess() {
      const url = this.getProcessDownloadState(this.processId)?.download_url;
      if (!url) {
        return this.snackToast.open({
          message: App.ProcessViewer.SNACK_TITLE_FAIL,
          type: "is-danger",
          duration: 8000
        });
      }
      downloadFile({
        attachment_name: `process_${this.$attrs.nup}.pdf`,
        url
      });
    },

    checkStep: debounce(async function() {
      const INTERVAL = maxNotificationWaitTime * 2;
      if (!this.finishedProcessAssembly && !this.failedProcessAssembly) {
        await this.getProcessBuildState(this.processId);
        setTimeout(this.checkStep, INTERVAL);
      }
    }, maxNotificationWaitTime)
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.list-doe-container {
  display: flex;
  flex-direction: column;
  max-height: 30vh;
  @media screen and (max-width: $tablet) {
    width: auto;
  }
  background-color: var(--color-1);
  color: var(--color-8);
  .alert {
    display: flex;
    align-items: center;
    background-color: var(--color-29);
  }
  @media screen and (max-width: $tablet) {
    width: auto;
  }
  ul {
    overflow-y: auto;
  }

  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.6rem;
    &:not(:last-child) {
      border-bottom: 1px solid $border-color;
    }
  }
}
</style>
